<template>
    <div>
        <hero-bar :has-right-visible="true" menu-icon="desktop-mac">
            {{ $lang.CircuitDevices.Title }}
            <span class="subtitle hero-subtitle">{{ $lang.CircuitDevices.Title_2 }}</span>

            <p v-if="backBtnActive == true || $store.state.isCircDevicesFromCalculation" class="control" slot="right" style="margin-right: 16px">
                <b-button class="button is-gray" @click="previous">{{ $lang.Menus.BackBtn }}</b-button>
            </p>
        </hero-bar>
        <section>
            <!-- Gapless: Bulma's negative column margins pushed the page 12px wider than the window. -->
            <div class="columns is-gapless">
                <div v-if="$store.state.isCircDevicesFromCalculation" class="column is-1" style="padding-left: 2rem; background-color: #FFFFFF; width: 10%; height: 100%; min-height: 100vh; display: flex; flex-direction: column; ">
                    <b-field>
                        <verticalStep-bar />
                    </b-field>
                </div>
                <!-- min-width: 0, or the flex column grows to the table's minimum instead of letting it scroll. -->
                <div class="column" style="min-width: 0">
                    <div class="section is-main-section table-page">
                        <!-- The same upload card as the member screen; here it adds devices directly. -->
                        <form class="upload-card" @submit.prevent="submit">
                            <b-upload v-model="dropFiles"
                                      class="upload-zone"
                                      multiple
                                      drag-drop
                                      expanded
                                      accept=".xlsx, .xls"
                                      :validationMessage="$lang.CircuitDevices.Validation"
                                      :loading="isSendLoading">
                                <div class="upload-zone-body">
                                    <span class="mdi mdi-upload" aria-hidden="true"></span>
                                    <span class="upload-zone-text">{{ $lang.CircuitDevices.Warning_1 }}</span>
                                    <span class="upload-zone-note">{{ $lang.CircuitDevices.Warning_2 }}</span>
                                </div>
                            </b-upload>

                            <div class="upload-side">
                                <div class="upload-files">
                                    <span v-for="(file, index) in dropFiles" :key="index" class="upload-file">
                                        <span class="mdi mdi-file-excel" aria-hidden="true"></span>
                                        <span class="upload-file-name">{{ file.name }} — {{ calcSize(file) }}</span>
                                        <button class="delete is-small" type="button" :title="$lang.CircuitDevices.Delete" @click="deleteDropFile(index)"></button>
                                    </span>
                                    <b-progress v-if="isSendLoading" type="is-primary" size="is-small"></b-progress>
                                </div>

                                <div class="upload-actions">
                                    <b-button icon-left="download" :loading="isLoading" @click="downloadExcel">
                                        {{ $lang.CircuitDevices.DownloadBtn }}
                                    </b-button>
                                    <b-button type="is-primary" icon-left="content-save" :loading="isSendLoading" @click="uploadExcel">
                                        {{ $lang.CircuitDevices.UploadBtnAdmin }}
                                    </b-button>
                                </div>
                            </div>
                        </form>

                        <div v-if="checkedRows.length > 0" class="bulk-bar">
                            <span><b>{{ $lang.CircuitDevices.TotalChecked }}</b>: {{ checkedRows.length }}</span>
                            <b-button class="is-danger" icon-left="delete" @click="deleteSelectedRows">
                                {{ $lang.CircuitDevices.DeleteChecked }}
                            </b-button>
                        </div>

                        <!--
                            Widths: Tip, Marka, Kod and Çekmeceli/Sabit share what is left; the rest
                            are fixed. The design folds the two dates into one column; they stay two,
                            because each one is searchable here.
                        -->
                        <b-table v-if="$store.state.memberId != 'TestMember'"
                                 class="data-table is-m-checkable is-sticky-lead-3"
                                 style="--table-min: 1240px"
                                 :data="devicesdata"
                                 :checked-rows.sync="checkedRows"
                                 :checkbox-position="checkboxPosition"
                                 :checkbox-type="checkboxType"
                                 checkable
                                 :paginated="isPaginated"
                                 :per-page="perPage"
                                 hoverable>
                            <b-table-column field="Id" label="Id" v-slot="props" width="64" numeric cell-class="m-corner">
                                {{ props.row.Id }}
                            </b-table-column>
                            <b-table-column field="Type" :label="$lang.CalcCircInfos.Type" v-slot="props" searchable cell-class="cell-strong m-title">
                                {{ props.row.Type }}
                            </b-table-column>
                            <!-- On a phone the code joins the brand on the subtitle line, as the design writes it. -->
                            <b-table-column field="Brand" :label="$lang.CalcCircInfos.Brand" v-slot="props" searchable cell-class="m-sub">
                                {{ props.row.Brand }}<span class="is-hidden-tablet"> — {{ props.row.Code }}</span>
                            </b-table-column>
                            <b-table-column field="Code" :label="$lang.CalcCircInfos.Code" v-slot="props" searchable cell-class="m-hide">
                                {{ props.row.Code }}
                            </b-table-column>
                            <!-- The header draws I<sub>N</sub>; the plain label is what a phone card shows. -->
                            <b-table-column field="In_Value" :label="$lang.CalcCircInfos.In_A.replace(/<[^>]+>/g, '')" searchable width="82" numeric>
                                <template v-slot:header="">
                                    <span v-html="$lang.CalcCircInfos.In_A"></span>
                                </template>
                                <template v-slot="props">
                                    {{ props.row.In_Value }}
                                </template>
                            </b-table-column>
                            <b-table-column field="Pole" :label="$lang.CalcCircInfos.PoleNumber" v-slot="props" searchable width="94" numeric>
                                {{ props.row.Pole }}
                            </b-table-column>
                            <b-table-column field="Sbt_Cekmeceli" :label="$lang.CalcCircInfos.Sbt_cekmeceli" v-slot="props" searchable>
                                {{ props.row.Sbt_Cekmeceli }}
                            </b-table-column>
                            <b-table-column field="Power" :label="$lang.CalcCircInfos.Power" v-slot="props" searchable width="92" numeric>
                                {{ props.row.Power }}
                            </b-table-column>
                            <b-table-column field="Pv" :label="$lang.CircuitDevices.Pv" v-slot="props" searchable width="110" numeric>
                                {{ props.row.Pv }}
                            </b-table-column>
                            <b-table-column field="CreateDate" :label="$lang.CalcCircInfos.CreateDate" v-slot="props" searchable width="120" numeric>
                                {{ props.row.CreateDate }}
                            </b-table-column>
                            <b-table-column field="UpdateDate" :label="$lang.CalcCircInfos.UpdateDate" v-slot="props" searchable width="120" numeric>
                                {{ props.row.UpdateDate }}
                            </b-table-column>

                            <b-table-column field="Action" v-slot="props" width="120">
                                <div class="row-actions">
                                    <button class="button row-action" :title="$lang.MyProjects.Edit" @click="openUpdateModal(props.row)">
                                        <span class="mdi mdi-pencil" aria-hidden="true"></span>
                                        <span class="row-action-label">{{ $lang.MyProjects.Edit }}</span>
                                    </button>
                                    <button class="button row-action is-destructive" :title="$lang.CircuitDevices.Delete" @click="deleteRow(props.row)">
                                        <span class="mdi mdi-delete" aria-hidden="true"></span>
                                        <span class="row-action-label">{{ $lang.CircuitDevices.Delete }}</span>
                                    </button>
                                </div>
                            </b-table-column>

                            <template #bottom-left>
                                {{ devicesdata.length }} {{ $lang.CircuitDevices.CountSuffix }}
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>

            <b-modal v-model="isUpdateModalActive" has-modal-card :width="720">
                <div class="modal-card device-modal">
                    <header class="modal-card-head">
                        <p class="modal-card-title">{{ $lang.CircuitDevices.UpdateDevice }}</p>
                        <button type="button" class="delete" :aria-label="$lang.Menus.ModalCloseBtn" @click="isUpdateModalActive = false"></button>
                    </header>
                    <section class="modal-card-body">
                        <div class="member-grid">
                            <b-field :label="$lang.CalcCircInfos.Type">
                                <b-input v-model="deviceType"></b-input>
                            </b-field>
                            <b-field :label="$lang.CalcCircInfos.Brand">
                                <b-input v-model="deviceBrand"></b-input>
                            </b-field>
                            <b-field :label="$lang.CalcCircInfos.Code">
                                <b-input v-model="deviceCode"></b-input>
                            </b-field>
                            <b-field>
                                <template #label><span v-html="$lang.CalcCircInfos.In_A"></span></template>
                                <b-input v-model="deviceIn"></b-input>
                            </b-field>
                            <b-field :label="$lang.CalcCircInfos.PoleNumber">
                                <b-input v-model="devicePole"></b-input>
                            </b-field>
                            <b-field :label="$lang.CalcCircInfos.Sbt_cekmeceli">
                                <b-input v-model="sbt_cekmeceli"></b-input>
                            </b-field>
                            <b-field :label="$lang.CalcCircInfos.Power">
                                <b-input v-model="devicePower" type="number"></b-input>
                            </b-field>
                            <b-field :label="$lang.CircuitDevices.Pv">
                                <b-input v-model="devicePv" type="number"></b-input>
                            </b-field>
                        </div>
                    </section>
                    <footer class="modal-card-foot">
                        <b-button :label="$lang.Menus.ModalCloseBtn" @click="isUpdateModalActive = false" />
                        <b-button :label="$lang.Menus.ModalUpdtBtn" type="is-primary" @click="updateRow()" />
                    </footer>
                </div>
            </b-modal>
        </section>
    </div>
</template>

<script>
    import router from '@/router';
    import api from '@/api';
    import VerticalStepBar from '@/components/VerticalStepBar.vue';

    export default {
        components: {
            VerticalStepBar
        },
        data() {
            return {
                name: 'CircuitDevices',
                isLoading: false,
                isPaginated: true,
                perPage: 10,
                currentPage: 1,
                checkboxPosition: 'left',
                checkboxType: 'is-primary',
                checkedRows: [],
                devicesdata: [],
                dropFiles: [],
                formData: [],
                isSendLoading: false,
                loadPercentage: 0,
                isUpdateModalActive: false,
                deviceId: 0,
                deviceType: '',
                deviceBrand: '',
                deviceCode: '',
                deviceIn: '',
                devicePole: '',
                sbt_cekmeceli: '',
                devicePower: 0,
                devicePv: 0,
                backBtnActive: false
            };
        },
        computed: {
            newDataMsg() {
                return this.$lang.CircuitDevices.ExcelLoaded;
            }
        },
        watch: {
            dropFiles(newVal, oldVal) {
                if (newVal.at(-1)?.size > 3145728) {
                    this.$buefy.toast.open({
                        duration: 2500,
                        message: this.$lang.CircuitDevices.MbWarning_1 + " <b>3MB</b>" + this.$lang.CircuitDevices.MbWarning_2,
                        type: 'is-warning'
                    });
                    this.dropFiles.pop();
                }

                this.dropFiles.forEach((e, i) => {
                    if (!('previewUrl' in e)) {
                        e.previewUrl = URL.createObjectURL(e);
                    }
                });

                this.formData = [];
                this.dropFiles.forEach((e, i) => {
                    const objectModel = {
                        Id: i,
                        ItemId: this.$route.params.id,
                        Base64String: ''
                    };

                    const reader = new FileReader();
                    reader.readAsDataURL(e);
                    reader.onloadend = function () {
                        const base64data = reader.result.split(',')[1];
                        objectModel.Base64String = base64data;
                    };

                    this.formData.push(objectModel);
                });
            }
        },
        mounted() {
            if (this.$store.state.previousRoute == 'projectInfos' || this.$store.state.previousRoute == 'panelInfos' || this.$store.state.previousRoute == 'envConditions' ||
                this.$store.state.previousRoute == 'calc_Busbar' || this.$store.state.previousRoute == 'calc_InputCircuit' || this.$store.state.previousRoute == 'calc_OutputCircuit' ||
                this.$store.state.previousRoute == 'calc_Manuel' || this.$store.state.previousRoute == 'calc_Summary' || this.$store.state.previousRoute == 'results' ||
                this.$store.state.previousRoute == 'reportPdfPage' || this.$store.state.previousRoute == 'calculation') {
                this.backBtnActive = true;
            } else {
                this.backBtnActive = false;
            }

            this.$store.state.isFooterFixed = true;
            this.getTable();
        },
        destroyed() {
            this.$store.state.isFooterFixed = false;
        },
        methods: {
            getTable() {
                this.devicesdata = [];
                var langCode = this.$lang.Format.Lang;
                api.panels.getCircuitCalculationDevices(langCode)
                    .then((res) => {
                        console.log(res);
                        if (res.isError) {
                            console.log("error ");
                        } else {
                            res.data.forEach((e) => {
                                this.devicesdata.push(e);
                            });
                        }
                    })
                    .catch((error) => {
                        console.error("Error fetching data:", error);
                    });
            },
            downloadExcel() {
                this.isLoading = true;
                api.reportApi.getExcel_CircuitDevices('Panel', null, null, this.$lang.CircuitDevices.CircuitReport, this.$lang.Format.Lang, 'ExcelAdmin').then((res) => {
                    this.isLoading = false;
                }).catch((error) => {
                    this.isLoading = false;
                    this.$buefy.dialog.alert('An error occurred: ' + (error.response ? error.response.data.message : error.message));
                });
            },
            deleteDropFile(index) {
                this.dropFiles.splice(index, 1);
                this.formData.splice(index, 1);
            },
            calcSize(file) {
                const fileSize = Math.trunc(file.size / 1024);
                if (fileSize > 1024) {
                    const fileSizeMb = fileSize / 1024;
                    return `${fileSizeMb.toFixed(2)}MB`;
                } else {
                    return `${fileSize}kb`;
                }
            },
            uploadExcel() {
                const config = {
                    onUploadProgress: (progressEvent) => {
                        this.loadPercentage = Math.round((progressEvent.loaded * 50) / progressEvent.total);
                    },
                    onDownloadProgress: (progressEvent) => {
                        this.loadPercentage = Math.round((progressEvent.loaded * 50) / progressEvent.total) + 50;
                    }
                };
                this.isSendLoading = true;
                if (this.formData != null) {
                    api.reportApi.uploadExcel_CircuitDevices_Admin(this.formData, config).then((res) => {
                        this.isSendLoading = false;
                        if (res.isError) {
                            this.$buefy.dialog.alert(this.$errorModal(res));
                        } else {
                            this.formData = res.data;
                            this.$buefy.dialog.alert(this.$successModal(this.newDataMsg));
                            this.dropFiles = [];
                            this.formData = [];
                            this.loadPercentage = 0;
                            this.getTable();
                        }
                    });
                }
            },
            columnTdAttrs(row, column) {
                if (row.id === 'Total') {
                    if (column.label === 'ID') {
                        return {
                            colspan: 4,
                            class: 'has-text-weight-bold',
                            style: {
                                'text-align': 'left !important'
                            }
                        }
                    } else if (column.label === 'Gender') {
                        return {
                            class: 'has-text-weight-semibold'
                        }
                    } else {
                        return {
                            style: { display: 'none' }
                        }
                    }
                }
                return null
            },
            openUpdateModal(row) {
                this.isUpdateModalActive = true;
                this.deviceId = row.Id;
                this.deviceType = row.Type;
                this.deviceBrand = row.Brand;
                this.deviceCode = row.Code;

                if (row.In_Value != 'NA') {
                    this.deviceIn = row.In_Value;
                } else {
                    this.deviceIn = '';
                }

                if (row.Pole != 'NA') {
                    this.devicePole = row.Pole;
                } else {
                    this.devicePole = '';
                }

                if (row.Sbt_Cekmeceli != 'NA') {
                    this.sbt_cekmeceli = row.Sbt_Cekmeceli;
                } else {
                    this.sbt_cekmeceli = '';
                }

                if (row.Power != 'NA') {
                    this.devicePower = row.Power;
                } else {
                    this.devicePower = '';
                }

                if (row.Pv != 'NA') {
                    this.devicePv = row.Pv;
                } else {
                    this.devicePv = '';
                }
            },
            updateRow() {
                const row = {
                    Id: this.deviceId,
                    Type: this.deviceType,
                    Brand: this.deviceBrand,
                    Code: this.deviceCode,
                    In_Value: this.deviceIn,
                    Pole: this.devicePole,
                    Sbt_Cekmeceli: this.sbt_cekmeceli,
                    Power: this.devicePower,
                    Pv: this.devicePv,
                    CreateDate: null,
                    UpdateDate: null
                };

                api.panels.updateCircuitCalculationDevices(row, this.$lang.Format.Lang).then((res) => {
                    if (res.isError) {
                        this.$buefy.dialog.alert(this.$errorModal(res));
                    } else {
                        this.isUpdateModalActive = false;
                        this.$buefy.toast.open(this.$lang.OtherDialog.Updated);
                        this.getTable();
                    }
                });
            },
            deleteRow(row) {
                this.$buefy.dialog.confirm({
                    title: this.$lang.DeleteDialog.Title,
                    message: this.$lang.DeleteDialog.Message,
                    confirmText: this.$lang.DeleteDialog.Confirm,
                    cancelText: this.$lang.DeleteDialog.Cancel,
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () =>

                        api.panels.deleteFromCircuitCalculationDevices(row.Id).then((res) => {
                            if (res.isError) {
                                this.$buefy.dialog.alert(this.$errorModal(res));
                            } else {
                                this.$buefy.toast.open(this.$lang.DeleteDialog.Deleted);
                                this.getTable();
                            }
                        })
                })
            },
            deleteSelectedRows() {
                this.$buefy.dialog.confirm({
                    title: this.$lang.DeleteDialog.Title,
                    message: this.$lang.DeleteDialog.Message_3,
                    confirmText: this.$lang.DeleteDialog.Confirm_3,
                    cancelText: this.$lang.DeleteDialog.Cancel,
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () => {
                        api.panels.deleteFromCircuitCalculationDevices_2(this.checkedRows.map((r) => r.Id)).then((res) => {
                            if (res.isError) {
                                this.$buefy.dialog.alert(this.$errorModal(res));
                            } else {
                                this.$buefy.toast.open(this.$lang.DeleteDialog.Deleted);
                                this.getTable();
                                this.checkedRows = [];
                            }
                        })
                    }
                })
            },
            previous() {
                router.push('/' + this.$store.state.previousRoute);
            },
            formatDate(date) {
                if (date == null) {
                    return '-';
                } else {
                    var yil = date.getFullYear();
                    var ay = ('0' + (date.getMonth() + 1)).slice(-2);
                    var gun = ('0' + date.getDate()).slice(-2);
                    var saat = ('0' + date.getHours()).slice(-2);
                    var dakika = ('0' + date.getMinutes()).slice(-2);
                    var saniye = ('0' + date.getSeconds()).slice(-2);

                    var formatliTarih = yil + '-' + ay + '-' + gun + ' ' + saat + ':' + dakika + ':' + saniye;

                    return formatliTarih;
                }
            },
        }
    };
</script>
